import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for debounced value
 * @param {any} value - Value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {any} Debounced value
 */
export function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [value, delay]);
  
  return debouncedValue;
}

/**
 * Custom hook for debounced callback
 * @param {Function} callback - Callback function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced callback
 */
export function useDebouncedCallback(callback, delay = 300) {
  const timeoutRef = useRef(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debouncedCallback;
}

/**
 * Custom hook to detect if on mobile device
 * @returns {boolean} True if on mobile
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
}

/**
 * Custom hook for intersection observer (lazy loading)
 * @param {Object} options - IntersectionObserver options
 * @returns {[Function, boolean]} [ref setter, isIntersecting]
 */
export function useIntersectionObserver(options = {}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [element, setElement] = useState(null);
  
  useEffect(() => {
    if (!element) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, {
      threshold: 0.1,
      rootMargin: '50px',
      ...options
    });
    
    observer.observe(element);
    
    return () => observer.disconnect();
  }, [element, options]);
  
  return [setElement, isIntersecting];
}

/**
 * Custom hook for window size
 * @returns {{ width: number, height: number }}
 */
export function useWindowSize() {
  const [size, setSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });
  
  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return size;
}

/**
 * Custom hook for optimistic updates
 * @param {any} serverValue - Value from server
 * @returns {[any, Function, Function]} [value, setOptimistic, revert]
 */
export function useOptimisticUpdate(serverValue) {
  const [optimisticValue, setOptimisticValue] = useState(null);
  
  // Sync with server value when it changes
  useEffect(() => {
    setOptimisticValue(null);
  }, [serverValue]);
  
  const value = optimisticValue !== null ? optimisticValue : serverValue;
  
  const setOptimistic = useCallback((newValue) => {
    setOptimisticValue(newValue);
  }, []);
  
  const revert = useCallback(() => {
    setOptimisticValue(null);
  }, []);
  
  return [value, setOptimistic, revert];
}


// ============================================================
// PERFORMANCE METRICS TRACKING
// ============================================================

/**
 * Track and log performance metrics
 * @param {string} metricType - Type of metric (page_load, api_call, render, etc.)
 * @param {object} options - { page, component, metadata }
 */
export function usePerformanceTracker(metricType, options = {}) {
  const startTimeRef = useRef(null);
  const hasLoggedRef = useRef(false);
  
  const startTracking = useCallback(() => {
    startTimeRef.current = performance.now();
    hasLoggedRef.current = false;
  }, []);
  
  const endTracking = useCallback(async (additionalMetadata = {}) => {
    if (!startTimeRef.current || hasLoggedRef.current) return;
    
    const duration = performance.now() - startTimeRef.current;
    hasLoggedRef.current = true;
    
    // Only log if duration is meaningful (> 10ms)
    if (duration < 10) return;
    
    const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
    if (!token || !API) return;
    
    try {
      await fetch(`${API}/api/phs/metrics/performance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: metricType,
          page: options.page || window.location.pathname,
          component: options.component,
          duration: Math.round(duration),
          metadata: { ...options.metadata, ...additionalMetadata }
        })
      });
    } catch (err) {
      // Silent failure - don't impact UX for metrics
    }
  }, [metricType, options]);
  
  // Auto-track on mount/unmount for page loads
  useEffect(() => {
    if (metricType === 'page_load') {
      startTracking();
      // Log when component mounts (page rendered)
      const timer = setTimeout(() => endTracking(), 100);
      return () => clearTimeout(timer);
    }
  }, [metricType, startTracking, endTracking]);
  
  return { startTracking, endTracking };
}


// ============================================================
// VIRTUALIZED LIST HOOK
// ============================================================

/**
 * Hook for simple virtualized rendering of large lists
 * @param {Array} items - Full list of items
 * @param {number} itemHeight - Height of each item in pixels
 * @param {number} containerHeight - Height of scroll container
 * @param {number} overscan - Number of items to render outside viewport
 * @returns {object} { visibleItems, containerStyle, totalHeight, onScroll }
 */
export function useVirtualizedList(items, itemHeight, containerHeight, overscan = 5) {
  const [scrollTop, setScrollTop] = useState(0);
  
  const totalHeight = items.length * itemHeight;
  
  const visibleRange = useMemo(() => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(
      items.length,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
    );
    return { startIndex, endIndex };
  }, [scrollTop, itemHeight, containerHeight, items.length, overscan]);
  
  const visibleItems = useMemo(() => {
    return items.slice(visibleRange.startIndex, visibleRange.endIndex).map((item, index) => ({
      item,
      index: visibleRange.startIndex + index,
      style: {
        position: 'absolute',
        top: (visibleRange.startIndex + index) * itemHeight,
        height: itemHeight,
        width: '100%'
      }
    }));
  }, [items, visibleRange, itemHeight]);
  
  const onScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);
  
  const containerStyle = {
    position: 'relative',
    height: totalHeight,
    width: '100%'
  };
  
  return {
    visibleItems,
    containerStyle,
    totalHeight,
    onScroll,
    visibleRange
  };
}


// ============================================================
// PAGINATED DATA HOOK
// ============================================================

/**
 * Hook for paginated data fetching with infinite scroll support
 * @param {Function} fetchFn - Async function to fetch data (page, limit) => { items, hasMore }
 * @param {object} options - { initialLimit, dependencies }
 */
export function usePaginatedData(fetchFn, options = {}) {
  const { initialLimit = 20, dependencies = [] } = options;
  
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  
  // Load initial data
  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn(1, initialLimit);
      setItems(result.items || []);
      setHasMore(result.hasMore !== false);
      setPage(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchFn, initialLimit]);
  
  // Load more data
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await fetchFn(nextPage, initialLimit);
      setItems(prev => [...prev, ...(result.items || [])]);
      setHasMore(result.hasMore !== false);
      setPage(nextPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchFn, page, initialLimit, loadingMore, hasMore]);
  
  // Reset and reload
  const refresh = useCallback(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    loadInitial();
  }, [loadInitial]);
  
  // Auto-load on dependency change
  useEffect(() => {
    loadInitial();
  }, [...dependencies, loadInitial]);
  
  return {
    items,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh
  };
}


// ============================================================
// LAZY IMAGE HOOK
// ============================================================

/**
 * Hook for lazy loading images with placeholder
 * @param {string} src - Image source URL
 * @param {string} placeholder - Placeholder image/color
 */
export function useLazyImage(src, placeholder = 'data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==') {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [ref, isIntersecting] = useIntersectionObserver({ rootMargin: '100px' });
  
  const [currentSrc, setCurrentSrc] = useState(placeholder);
  
  useEffect(() => {
    if (!isIntersecting || loaded || !src) return;
    
    const img = new Image();
    img.onload = () => {
      setCurrentSrc(src);
      setLoaded(true);
    };
    img.onerror = () => {
      setError(true);
    };
    img.src = src;
  }, [isIntersecting, src, loaded]);
  
  return { ref, src: currentSrc, loaded, error };
}


export default {
  useDebounce,
  useDebouncedCallback,
  useIsMobile,
  useIntersectionObserver,
  useWindowSize,
  useOptimisticUpdate,
  usePerformanceTracker,
  useVirtualizedList,
  usePaginatedData,
  useLazyImage
};
